import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';

/**
 * The four languages the platform is translated into, matching the server's
 * `Language` enum and `public/locales/*`. Declaring them makes
 * `i18n.resolvedLanguage` trustworthy: otherwise the detector passes through
 * whatever the browser reports (such as `de-DE`), and i18next requests locale
 * files that 404 before falling back to English.
 */
export const SUPPORTED_LANGUAGES = ['en', 'es', 'fr', 'pt-br'] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    debug: true,
    lowerCaseLng: true,
    supportedLngs: [...SUPPORTED_LANGUAGES],
    // `pt-br` is a supported language in its own right, not a region of some
    // `pt` we ship — so never strip the region to find a match.
    nonExplicitSupportedLngs: false,
    load: 'currentOnly',
    // React escapes rendered text, so escaping here would show a name like
    // O'Brien as `O&#39;Brien`. A `t()` result must therefore never reach
    // `dangerouslySetInnerHTML`.
    interpolation: { escapeValue: false },
  });

/**
 * The language code to send to the API, always one of `SUPPORTED_LANGUAGES`.
 *
 * Uses `resolvedLanguage`, what i18next settled on, rather than `language`,
 * what was asked for (possibly `de-DE`). Sending `language` could leave the UI
 * in English while the server answered in another language.
 */
export function apiLanguage(): SupportedLanguage {
  const resolved = i18n.resolvedLanguage ?? i18n.language;
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(resolved)
    ? (resolved as SupportedLanguage)
    : 'en';
}

export default i18n;
